import moment from 'moment';
import * as Yup from 'yup';
import { formatDate } from '@/utilities/date-helpers';
import {
  parseDateString,
  POSSIBLE_DATE_FORMAT,
  isEmpty,
  timeValidation,
  checkIfTimeIsPast,
} from '../../components/solicitation-form-context';
import { getTimestampFromDateNTime } from '../../utils/open-reopen-period-util';

export const parseDateStringLocal = (value, originalValue) => {
  if (!originalValue) return null;
  const parsedDate =
    originalValue instanceof Date && originalValue
      ? originalValue
      : moment(originalValue, POSSIBLE_DATE_FORMAT, true).toDate();
  return parsedDate;
};

export const getEditReopenPeriodYupSchema = ({
  rawPreviousPeriodData,
  previousPeriodData,
  rawNextPeriodData,
  readonlyFields,
} = {}) => {
  const getMinDate = () => {
    return moment().isAfter(previousPeriodData?.endDate)
      ? formatDate()
      : formatDate(previousPeriodData?.endDate);
  };
  return Yup.object().shape({
    startDate: readonlyFields?.includes('startDate')
      ? Yup.date().required('Posting date is required.')
      : Yup.date()
          .required('Posting date is required.')
          .typeError(' ')
          .transform(parseDateString)
          .nullable()
          .min(getMinDate(), () => {
            if (moment().isAfter(previousPeriodData?.endDate)) {
              return `Posting date should not be less than the current date.`;
            }
            return `The new Reopen period added overlaps with an existing ${
              rawPreviousPeriodData?.endDate ? 'Reopen' : 'Open'
            } period.`;
          }),

    startTime: readonlyFields?.includes('startTime')
      ? Yup.string().required('Posting time is required.')
      : Yup.string()
          .required('Posting time is required.')
          .when('startDate', {
            is: (startDate) => startDate && moment(startDate).isValid(),
            then: Yup.string()
              .required('Posting time is required.')
              .test({
                name: 'min-start-time',
                skipAbsent: true,
                test(startTime, { createError, parent }) {
                  let testRes = true;
                  const { startDate } = parent || {};
                  const startDateTime = getTimestampFromDateNTime(
                    startDate,
                    startTime,
                  );
                  const previousPeriodDateTime = getTimestampFromDateNTime(
                    previousPeriodData?.endDate,
                    previousPeriodData?.endTime,
                  );
                  if (moment().isAfter(moment(startDateTime), 'minutes')) {
                    testRes = createError({
                      message:
                        'Posting time should not be less than the current time.',
                    });
                  }
                  if (
                    moment(startDateTime).isSameOrBefore(
                      moment(previousPeriodDateTime),
                      'minutes',
                    )
                  ) {
                    testRes = createError({
                      message: `The new Reopen period added overlaps with an existing ${
                        rawPreviousPeriodData?.endDate ? 'Reopen' : 'Open'
                      } period.`,
                    });
                  }

                  return testRes;
                },
              }),
          }),
    endDate: Yup.date()
      .required('Closing date is required.')
      .typeError('Closing time is required.')
      .transform(parseDateString)
      .nullable()
      .when('startDate', {
        is: (startDate) => startDate && moment(startDate).isValid(),
        then: Yup.date()
          .required('Closing date is required.')
          .typeError('Closing time is required.')
          .transform(parseDateString)
          .nullable()
          .min(
            Yup.ref('startDate'),
            'Closing date must be after posting date.',
          ),
      }),
    endTime: Yup.string()
      .required('Closing time is required.')
      .when(['startDate', 'endDate', 'startTime'], {
        is: (startDate, endDate, startTime) =>
          startDate &&
          endDate &&
          startTime &&
          moment(startDate).isValid() &&
          moment(endDate).isValid(),
        then: Yup.string()
          .required('Closing time is required.')
          .test({
            name: 'is-valid-date-range',
            skipAbsent: true,
            test(endTime, { createError, parent }) {
              let testRes = true;
              const { endDate, startDate, startTime } = parent || {};
              const startDateTime = getTimestampFromDateNTime(
                startDate,
                startTime,
              );
              const endtDateTime = getTimestampFromDateNTime(endDate, endTime);
              if (moment().isAfter(moment(endtDateTime), 'minutes')) {
                testRes = createError({
                  message:
                    'Closing time should not be less than the current time.',
                });
              }
              if (
                moment(startDateTime).isSame(endtDateTime, 'day') &&
                moment(startDateTime).isAfter(endtDateTime)
              ) {
                testRes = createError({
                  message: 'Closing time must be after the posting time.',
                });
              }
              if (
                rawNextPeriodData?.startDate &&
                moment(endtDateTime).isSameOrAfter(
                  rawNextPeriodData?.startDate,
                  'minutes',
                )
              ) {
                testRes = createError({
                  message:
                    'The new Reopen period added overlaps with an existing Reopen period.',
                });
              }
              return testRes;
            },
          }),
      }),
  });
};

export const getAddReopenPeriodYupSchema = ({
  openPeriod,
  reopenPeriodListDescendingOrder,
  newReopenPeriodMinTimestamp,
  newReopenPeriodMinDateFormated,
  solicitation,
} = {}) => {
  return Yup.object().shape({
    startDate: Yup.date().when('rawSolPeriod.status', {
      is: (status) => status !== 'ACTIVE',
      then: Yup.date()
        .required('Posting date is required.')
        .typeError(' ')
        .transform(parseDateStringLocal)
        .nullable()
        .min(
          newReopenPeriodMinDateFormated
            ? moment(newReopenPeriodMinDateFormated, 'MM/DD/YYYY').toDate()
            : new Date(),
          () => {
            if (
              !newReopenPeriodMinDateFormated ||
              moment().isAfter(
                moment(newReopenPeriodMinDateFormated, 'MM/DD/YYYY'),
              )
            ) {
              return `Posting date should not be less than the current date.`;
            }
            const periodType = reopenPeriodListDescendingOrder?.[0]?.endDate
              ? 'Reopen'
              : 'Open';
            return solicitation?.contracts?.length > 0
              ? 'The new Mid-cycle period added overlaps with an existing Mid-cycle period.'
              : `The new Reopen period added overlaps with an existing ${periodType} period.`;
          },
        ),
    }),

    endDate: Yup.date()
      .required('Closing date is required.')
      .typeError(' ')
      .transform(parseDateStringLocal)
      .nullable()
      .when('startDate', {
        is: (startDate) => !isEmpty(startDate),
        then: Yup.date()
          .required('Closing date is required.')
          .typeError(' ')
          .transform(parseDateStringLocal)
          .nullable()
          .test(
            'after-start-date',
            'Closing date must be after posting date.',
            function (value) {
              // eslint-disable-next-line react/no-this-in-sfc
              const { startDate } = this.parent || {};
              if (!value || !startDate) return true;
              return moment(value).isSameOrAfter(moment(startDate), 'day');
            },
          ),
      }),
    startTime: Yup.string()
      .required('Posting time is required.')
      .when(['startDate'], {
        is: (startDate) =>
          moment(startDate).isSame(new Date(), 'day') && startDate,
        then: Yup.string()
          .required('Posting time is required.')
          .test(
            'is-valid',
            'Posting time cannot be in the past.',
            checkIfTimeIsPast,
          ),
      })
      .when(['startDate', 'endDate'], {
        is: (startDate, endDate) =>
          !isEmpty(endDate) &&
          moment(startDate).isSame(moment(newReopenPeriodMinDateFormated)),
        then: Yup.string()
          .required('Posting time is required.')
          .test(
            'min-start-time',
            'Posting time must be after the last open period.',
            function (value) {
              // eslint-disable-next-line react/no-this-in-sfc
              const { startDate } = this.parent || {};
              return moment(
                getTimestampFromDateNTime(startDate, value),
              ).isAfter(moment(newReopenPeriodMinTimestamp));
            },
          ),
      })
      .test({
        name: 'min-start-time',
        skipAbsent: true,
        test(startTime, { createError, parent }) {
          let testRes = true;
          const { startDate } = parent || {};
          const startDateTime = getTimestampFromDateNTime(startDate, startTime);
          if (moment().isAfter(moment(startDateTime), 'minutes')) {
            testRes = createError({
              message: 'Posting time should not be less than the current time.',
            });
          }
          if (
            moment(startDateTime).isSameOrBefore(
              moment(newReopenPeriodMinTimestamp),
              'minutes',
            )
          ) {
            const periodType = reopenPeriodListDescendingOrder?.[0]?.endDate
              ? 'Reopen'
              : 'Open';
            testRes = createError({
              message:
                solicitation.contracts.length > 0
                  ? 'The new Mid-cycle period added overlaps with an existing Mid-cycle period.'
                  : `The new Reopen period added overlaps with an existing ${periodType} period.`,
            });
          }
          return testRes;
        },
      }),
    endTime: Yup.string()
      .required('Closing time is required.')
      .when(['endDate'], {
        is: (endDate) => moment(endDate).isSame(new Date(), 'day') && endDate,
        then: Yup.string()
          .required('Closing time is required.')
          .test(
            'is-valid',
            'Closing time cannot be in the past.',
            checkIfTimeIsPast,
          ),
      })
      .when(['startDate', 'endDate', 'startTime'], {
        is: (startDate, endDate, startTime) =>
          moment(startDate).isSame(endDate, 'day') && startTime,
        then: Yup.string()
          .required('Closing time is required.')
          .test(
            'is-greater',
            'Closing time must be after posting time.',
            timeValidation,
          ),
      }),
  });
};
